import * as ko from 'knockout';
import LZString from 'lz-string';
import { ppFetch } from '../shared/pp-fetch';
import { SchedulePickerBase } from '../shared/schedule-picker-base';
import { NhlTeam } from './models/team';
import { NhlGame } from './models/game';
import { NhlWeek } from './models/week';
import { Series } from '../shared/models/series';
import { SelectionType } from '../shared/enums/selection-type';
import { RankMethod } from '../shared/enums/rank-method';
import { Conferences } from './enums';
import { Enumerable } from '../shared/linq';
import { userConfigManager } from '../shared/user-config-manager';
import { readConfig, getApimBaseUrl, getWebAppBaseUrl } from '../shared/config';
import { formatMonthOrdinalDay } from '../shared/date-helpers';
import type { IGame } from '../shared/interfaces/IGame';
import type { ITeam } from '../shared/interfaces/ITeam';
import type { ISeries } from '../shared/interfaces/ISeries';
import type { NhlTiebreakerTeam, GameResult } from '../shared/tiebreaker/types';
import { nhlConferenceRanker } from './tiebreaker/nhl-tiebreaker';
import { ArrayGameResultsLookup } from '../shared/tiebreaker/game-results-lookup';
import {
  toNhlTiebreakerTeam,
  buildTeamObsMap,
  applyTiebreakerResult,
  reorderTeamObs,
} from '../shared/tiebreaker/adapters/ko-adapter';
import { mapDictionaryToArray, rankSort, divisionRankSort } from '../shared/helpers';
import * as Information from './schedule-info';
import { BracketGenerator } from './bracket-generator';
import { applyOfficialPlayoffOutcomes } from './official-playoff-overlay';
import { shouldApplyOfficialOverlay } from '../shared/should-apply-official-overlay';

// Re-export for entry point use
export { NhlApp };

// Expose static helpers on window for legacy Knockout template bindings
function exposeToWindow(app: NhlApp): void {
  (window as any).SchedulePicker = (window as any).SchedulePicker ?? {};
  (window as any).SchedulePicker.NHL = (window as any).SchedulePicker.NHL ?? {};
  (window as any).SchedulePicker.NHL.App = {
    mapDictionaryToArray: mapDictionaryToArray,
    RankSort: rankSort,
    DivisionRankSort: divisionRankSort,
  };
  // Expose SelectionType on $root (accessed as $root.SelectionType in KO templates)
  (window as any).SelectionType = SelectionType;
  // Expose vm globally as the Knockout views reference vm.xxx
  (window as any).vm = app;
}

type NhlViewMode = 'division' | 'conference' | 'wildcard';

class NhlApp extends SchedulePickerBase {
  ConferenceTeams: ConferenceTeamDictionary;
  Teams: { key: string; value: Record<string, Record<string, KnockoutObservable<ITeam>>> }[];
  Dict: TeamDictionary;
  WeekDateMap: Record<number, any>;
  WesternTop: KnockoutObservable<KnockoutObservable<ITeam>[]>;
  EasternTop: KnockoutObservable<KnockoutObservable<ITeam>[]>;
  RNHLStyles: KnockoutObservable<boolean>;
  Warning: KnockoutObservable<string>;
  SkipDivisionRank: KnockoutObservable<boolean>;
  SeasonYearEnd = 2017;
  HtmlExport: KnockoutObservable<string>;
  Export: KnockoutObservable<string>;
  NumGames: KnockoutObservable<number>;
  ShowDivision: KnockoutObservable<boolean>;
  HideStatus: KnockoutObservable<boolean>;
  WildcardCentral: KnockoutObservableArray<KnockoutObservable<NhlTeam>>;
  WildcardPacific: KnockoutObservableArray<KnockoutObservable<NhlTeam>>;
  WildcardWestern: KnockoutObservableArray<KnockoutObservable<NhlTeam>>;
  WildcardMetropolitan: KnockoutObservableArray<KnockoutObservable<NhlTeam>>;
  WildcardAtlantic: KnockoutObservableArray<KnockoutObservable<NhlTeam>>;
  WildcardEastern: KnockoutObservableArray<KnockoutObservable<NhlTeam>>;
  DivisionStartPosition: KnockoutObservable<number>;
  ConferenceChampionshipStartPosition: KnockoutObservable<number>;
  FinalsStartPosition: KnockoutObservable<number>;
  static postSeasonWeeks = 4;
  GameScheduleGameMap: GameScheduleMap = {};
  ViewMode: KnockoutObservable<NhlViewMode>;
  StickyColumns: KnockoutObservable<boolean>;
  GameArray: number[];
  Timeout: ReturnType<typeof setTimeout> | undefined;
  randomCallback: ReturnType<typeof setTimeout> | undefined;
  /** Cached official playoff data from the initial league fetch. Populated
   *  on ppFetch resolution and re-applied via the UrlString subscriber on
   *  every reload — so both initial load AND "Current Standings" reset
   *  hit the overlay. Defaults to an empty Playoffs so the overlay runs
   *  the same code path before the league response arrives. */
  cachedOfficialPlayoffs: ILeaguePlayoffs = { series: [] };

  constructor() {
    const config = readConfig();
    super('hockey', config);
    const self = this;

    self.ViewMode = ko.observable<NhlViewMode>(
      userConfigManager.UserConfig.NHL?.ViewMode ?? 'division'
    );
    self.ViewMode.subscribe((v) => {
      if (userConfigManager.UserConfig.NHL) {
        userConfigManager.UserConfig.NHL.ViewMode = v;
        userConfigManager.saveStorage();
      }
      document.querySelectorAll('div.open').forEach((el) => el.classList.remove('open'));
    });

    self.Hash = ko.observable(window.location.hash);
    self.WildcardCentral = ko.observableArray([]);
    self.WildcardPacific = ko.observableArray([]);
    self.WildcardWestern = ko.observableArray([]);
    self.WildcardMetropolitan = ko.observableArray([]);
    self.WildcardAtlantic = ko.observableArray([]);
    self.WildcardEastern = ko.observableArray([]);
    self.DivisionStartPosition = ko.observable(0);
    self.ConferenceChampionshipStartPosition = ko.observable(0);
    self.FinalsStartPosition = ko.observable(0);

    self.StickyColumns = ko.observable(
      userConfigManager.UserConfig.NHL?.StickyColumns ?? false
    );
    if (self.StickyColumns()) {
      document.querySelectorAll('footer div.row > div').forEach((el) => {
        el.classList.add('col-md-push-2');
        el.classList.remove('col-sm-10');
        el.classList.add('col-sm-6');
        el.classList.add('col-md-9');
      });
    } else {
      document.querySelectorAll('footer div.row > div').forEach((el) => el.classList.remove('col-sm-push-2'));
    }
    self.StickyColumns.subscribe((newVal: boolean) => {
      if (newVal) {
        document.querySelectorAll('footer div.row > div').forEach((el) => el.classList.add('col-md-push-2'));
      } else {
        document.querySelectorAll('footer div.row > div').forEach((el) => el.classList.remove('col-md-push-2'));
      }
      if (userConfigManager.UserConfig.NHL && newVal !== userConfigManager.UserConfig.NHL.StickyColumns) {
        userConfigManager.UserConfig.NHL.StickyColumns = newVal;
        userConfigManager.saveStorage();
      }
    });

    self.GameArray = [];
    self.HideStatus = ko.observable(
      userConfigManager.UserConfig.NHL?.HideStatus ?? false
    );
    self.HideStatus.subscribe((newVal: boolean) => {
      if (userConfigManager.UserConfig.NHL && newVal !== userConfigManager.UserConfig.NHL.HideStatus) {
        userConfigManager.UserConfig.NHL.HideStatus = newVal;
        userConfigManager.saveStorage();
      }
    });

    self.ShowDivision = ko.observable(
      userConfigManager.UserConfig.NHL?.DivToggle ?? true
    );
    self.NumberOfTeams(32);
    self.Warning = ko.observable('');
    self.HtmlExport = ko.observable('');
    self.Export = ko.observable('');
    self.EmptySeasonString = 'Aw18ZXTt-DFOS1b0c17Pd-wYUcSaWeRZVdTbXfQ408y5UA';
    self.NumGames = ko.observable(0);

    super.startComputing();
    self.SaveString.subscribe(() => {
      self.setExport();
    });

    // Shared handler for league data loading (V2 API data normalized to V1 format)
    const handleLeagueData = (
      leagueName: string,
      seasonYear: number,
      currentWeekString: string,
      isCustomLeague: boolean,
      westernTeams: ITeamData[],
      easternTeams: ITeamData[],
      games: IGameData[],
      replaceTeams: any[]
    ) => {
      if (seasonYear) {
        self.SeasonYearEnd = seasonYear;
      }
      if (currentWeekString) {
        self.CurrentSeasonString(currentWeekString);
      }
      self.LeagueName(leagueName);
      self.IsCustomLeague(isCustomLeague);

      const dict: TeamDictionary = {};
      let teamObj: KnockoutObservable<ITeam>;
      self.ConferenceTeams = {} as ConferenceTeamDictionary;
      self.ConferenceTeams['Western'] = {} as Record<string, KnockoutObservable<ITeam>>;
      dict['Western'] = {} as Record<string, Record<string, KnockoutObservable<ITeam>>>;
      ko.utils.arrayForEach(westernTeams, (team: ITeamData) => {
        dict['Western'][team.Division] = dict['Western'][team.Division] || {};
        teamObj = ko.observable(
          new NhlTeam(team.TeamAbbreviation, team.RegionName, team.TeamName, team.Conference, team.Division, team.TeamID, team.TeamLogo)
        );
        dict['Western'][team.Division][team.TeamAbbreviation] = teamObj;
        self.ConferenceTeams['Western'][team.TeamAbbreviation] = teamObj;
      });

      dict['Eastern'] = {} as Record<string, Record<string, KnockoutObservable<ITeam>>>;
      self.ConferenceTeams['Eastern'] = {} as Record<string, KnockoutObservable<ITeam>>;
      ko.utils.arrayForEach(easternTeams, (team: ITeamData) => {
        teamObj = ko.observable(
          new NhlTeam(team.TeamAbbreviation, team.RegionName, team.TeamName, team.Conference, team.Division, team.TeamID, team.TeamLogo)
        );
        dict['Eastern'][team.Division] = dict['Eastern'][team.Division] || {};
        dict['Eastern'][team.Division][team.TeamAbbreviation] = teamObj;
        self.ConferenceTeams['Eastern'][team.TeamAbbreviation] = teamObj;
      });

      self.Dict = dict;
      self.Teams = mapDictionaryToArray(self.Dict);
      self.Weeks = Information.GetSchedule(self.Dict, games);
      self.MaxWeek(Enumerable.From(games).Max((g: any) => g.Week));
      self.MaxWeek.valueHasMutated();
      self.MaxWeek.notifySubscribers();
      self.TotalWeeks = self.MaxWeek() + NhlApp.postSeasonWeeks;
      self.setGameScheduleNumbers(games.length + 113);

      self.WeekDateMap = {} as Record<number, any>;
      for (const weekIdx in self.Weeks) {
        self.WeekDateMap[weekIdx] = self.WeekDateMap[weekIdx] || {};
        const week = self.Weeks[weekIdx];
        let weekGames = (week[0] as NhlWeek).games().concat((week[1] as NhlWeek).games());
        if (week.length === 3) {
          weekGames = weekGames.concat((week[2] as NhlWeek).games());
        }
        const orderedGames = Enumerable.From(weekGames)
          .Where((p: any) => p != undefined)
          .OrderBy((p: KnockoutObservable<IGame>) => p().SortOrder()) as any;
        const weekIdxCopy = weekIdx;
        if (orderedGames.Any()) {
          self.WeekDateMap[weekIdx].DayString = ko.computed(() => {
            const f = orderedGames.First()().gameDate();
            const l = orderedGames.Last()().gameDate();
            return `Week ${weekIdxCopy}: ${f ? formatMonthOrdinalDay(f) : ''} - ${l ? formatMonthOrdinalDay(l) : ''}`;
          });
        } else {
          self.WeekDateMap[weekIdx].DayString = ko.computed(() => `Week ${weekIdxCopy}`);
        }
        for (const gameObs of weekGames) {
          const game = gameObs();
          self.GameScheduleGameMap[game.gameScheduleNum()] = gameObs;
        }
      }

      // Ensure post-season week entries exist before assigning DayString
      for (let i = 1; i <= NhlApp.postSeasonWeeks; i++) {
        self.WeekDateMap[self.MaxWeek() + i] = self.WeekDateMap[self.MaxWeek() + i] || {};
      }
      self.WeekDateMap[self.MaxWeek() + 1].DayString = ko.computed(() => 'First Round');
      self.WeekDateMap[self.MaxWeek() + 2].DayString = ko.computed(() => 'Second Round');
      self.WeekDateMap[self.MaxWeek() + 3].DayString = ko.computed(() => 'Conference Finals');
      self.WeekDateMap[self.MaxWeek() + 4].DayString = ko.computed(() => 'Stanley Cup Finals');

      self.SelectedWeek({ key: 1, value: self.Weeks[1] });
      self.NumGames(games.length);

      self.Warning.subscribe((newVal) => {
        if (newVal) {
          // Bootstrap alert plugin invocation removed (jQuery dependency eliminated)
        }
      });
      self.WesternTop = ko.observable([]);
      self.EasternTop = ko.observable([]);
      self.RNHLStyles = ko.observable(false);
      self.RNHLStyles.subscribe(() => {
        self.setExport();
      });
      self.SkipDivisionRank = ko.observable(false);

      self.init(replaceTeams);

      if (window.location.hash) {
        try {
          const teamName = window.location.hash.slice(1);
          const searchTeam = self.findTeam(teamName);
          if (searchTeam) {
            self.SelectedTeam(searchTeam());
          }
        } catch (ex) {
          console.warn(ex);
        }
      }

      self.UrlString.subscribe((newString) => {
        if (newString && newString.length) {
          newString = newString.replace(/%20/gi, '+');
          const decompress = LZString.decompressFromEncodedURIComponent(newString);
          if (decompress) {
            self.load(decompress);
            // Apply the cached official playoff overlay ONLY when this load
            // represents the official current state — i.e., the loaded save
            // string equals CurrentSeasonString (the server-emitted current
            // week save string). User-modified loads from `?L=`, localStorage,
            // or watchlists must NOT be overwritten by IRL outcomes. The gate
            // covers both the initial ppFetch path (UrlString set from
            // `?L=`/localStorage matches CurrentSeasonString iff the user has
            // not modified picks) and the "Current Standings" reset path
            // (setCurrentSeason → save-strings fetch → UrlString set to
            // CurrentSeasonString → here → match → overlay).
            if (shouldApplyOfficialOverlay(newString, self.CurrentSeasonString())) {
              applyOfficialPlayoffOutcomes(self, self.cachedOfficialPlayoffs);
            }

            // Auto-advance SelectedWeek to the latest playoff round that has
            // any selection made. Walks Finals → Conference Finals → Round 2
            // → Round 1; the first round with a pick wins. This lets users
            // land on the most-recent activity rather than the regular-season
            // tail when official outcomes drop them mid-bracket.
            //
            // Important: NHL playoff games live inside `slot.Series()[i].games()`,
            // NOT in `slot.GamesComputed()`. WeekBase.GamesComputed reads the
            // flat `slot.games` observable used by regular-season weeks; for
            // playoff weeks that array is empty and the actual best-of-7 games
            // hang off each Series object instead.
            const maxWeek = self.MaxWeek();
            // Skip auto-advance when the regular season is COMPLETELY empty
            // (no picks). On a fresh reset / empty save string, the picker's
            // default bracket is built from arbitrary tiebreaker output (all
            // teams 0-0-0); the cached official Playoffs can apply R1
            // outcomes by coincidence when team pairs happen to overlap.
            // Without this gate, a user who clears all picks would be jumped
            // to a playoff round instead of week 1.
            //
            // Once the user has any reg-season state — partial WIP or the
            // full official load — the bracket reflects something meaningful
            // and we want to land on the latest playoff round with activity.
            const numGames = self.NumGames();
            let regSeasonHasAny = false;
            for (let i = 0; i < numGames; i++) {
              if (self.GameArray[i]) {
                regSeasonHasAny = true;
                break;
              }
            }
            if (!regSeasonHasAny) {
              return;
            }
            for (let weekIdx = self.TotalWeeks; weekIdx > maxWeek; weekIdx--) {
              const weekSlots = self.Weeks[weekIdx];
              if (!weekSlots) {
                continue;
              }
              let hasSelection = false;
              for (const slot of weekSlots) {
                const series = (slot as NhlWeek).Series();
                for (const s of series) {
                  for (const gameObs of s.games()) {
                    if (gameObs().selectionMade()) {
                      hasSelection = true;
                      break;
                    }
                  }
                  if (hasSelection) {
                    break;
                  }
                }
                if (hasSelection) {
                  break;
                }
              }
              if (hasSelection) {
                self.SelectedWeek({ key: weekIdx, value: self.Weeks[weekIdx] });
                // Loop walks high→low; the first hit IS the latest round with
                // picks. Always set BiggestWeek to it (not conditional) so the
                // downstream setCurrentSeason switchContext lands here.
                self.BiggestWeek(weekIdx);
                break;
              }
            }
          }
        } else {
          (window as any).PlayoffPredictor?.Spin?.hide?.();
        }
      });

      if (self.LoadedWatchlist()) {
        self.UrlString(self.LoadedWatchlist().SaveString());
      } else {
        self.UrlString(SchedulePickerBase.getQueryVariable('L') || '' || localStorage.getItem((window as any).leagueGuid));
      }

      document.querySelectorAll('#content .hidden').forEach((el) => el.classList.remove('hidden'));
      const loadingEl = document.getElementById('loading');
      if (loadingEl) loadingEl.style.display = 'none';

      setTimeout(() => {}, 80);

      ko.applyBindings(self, document.getElementById('content'));
      exposeToWindow(self);

      const navbarsForPadding = document.querySelectorAll('.navbar');
      const nav0Height = (navbarsForPadding[0] as HTMLElement)?.offsetHeight ?? 0;
      const nav1Height = (navbarsForPadding[1] as HTMLElement)?.offsetHeight ?? 0;
      document.body.style.paddingTop = (nav0Height + nav1Height) + 'px';

      super.initTeamSubscribers();
      self.setExport();
      super.finishComputing();
    };

    // All official leagues load from V2 API (no custom leagues for NHL)
    const { sportId, leagueGuid } = config;
    (window as any).leagueGuid = leagueGuid;
    ppFetch<any>({
      url: `${getApimBaseUrl()}/LeagueEngine/leagues/${sportId}/${leagueGuid}/official`,
      method: 'GET',
    }).then((result) => {
      // Cache official playoff data BEFORE handleLeagueData triggers UrlString,
      // so the UrlString subscriber's overlay call sees the populated cache on
      // initial load. Subsequent reloads (Current Standings reset, etc.) reuse
      // the same cache.
      self.cachedOfficialPlayoffs = result.league.playoffs ?? { series: [] };

      const teamById: Record<number, ITeamDataV2> = {};
      result.teams.forEach((t: ITeamDataV2) => { teamById[t.teamId] = t; });
      const westernTeams: ITeamData[] = result.teams
        .filter((t: ITeamDataV2) => t.conference === 'Western')
        .map((t: ITeamDataV2) => ({
          TeamAbbreviation: t.teamAbbreviation,
          RegionName: t.regionName,
          TeamName: t.teamName,
          Conference: t.conference,
          Division: t.division,
          TeamID: t.teamId,
          TeamLogo: t.teamLogo,
        } as ITeamData));
      const easternTeams: ITeamData[] = result.teams
        .filter((t: ITeamDataV2) => t.conference === 'Eastern')
        .map((t: ITeamDataV2) => ({
          TeamAbbreviation: t.teamAbbreviation,
          RegionName: t.regionName,
          TeamName: t.teamName,
          Conference: t.conference,
          Division: t.division,
          TeamID: t.teamId,
          TeamLogo: t.teamLogo,
        } as ITeamData));
      const v1Games: IGameData[] = result.league.games
        .filter((g: IGameDataV2) => teamById[g.homeId] && teamById[g.awayId])
        .map((g: IGameDataV2) => {
        const home = teamById[g.homeId];
        const away = teamById[g.awayId];
        return {
          Home: {
            TeamAbbreviation: home.teamAbbreviation,
            RegionName: home.regionName,
            TeamName: home.teamName,
            Conference: home.conference,
            Division: home.division,
            TeamID: home.teamId,
            TeamLogo: home.teamLogo,
          },
          Away: {
            TeamAbbreviation: away.teamAbbreviation,
            RegionName: away.regionName,
            TeamName: away.teamName,
            Conference: away.conference,
            Division: away.division,
            TeamID: away.teamId,
            TeamLogo: away.teamLogo,
          },
          Week: g.week,
          GameTypeID: 0,
          GameDate: g.gameDate,
          GameKey: g.gameKey,
          GameGUID: g.gameGuid,
          GameScheduleNum: g.gameScheduleNum,
        } as IGameData;
      });
      handleLeagueData(
        result.league.leagueName,
        result.league.superbowlYear,
        result.league.currentWeekString,
        false,
        westernTeams,
        easternTeams,
        v1Games,
        []
      );

      // Note: the official playoff overlay used to be applied here explicitly,
      // but is now wired into the UrlString subscriber (above) so it covers
      // BOTH this initial-load path AND the "Current Standings" reset path
      // (which goes through setCurrentSeason → save-strings fetch → UrlString
      // update → subscriber → load → overlay). The cache populated at the top
      // of this .then() is already in place by the time handleLeagueData
      // triggers the first UrlString update.
    }).catch((err: unknown) => {
      const e = err as Error;
      console.error('NHL schedule-picker load error:', e?.message, e?.stack, err);
      const loadingEl = document.getElementById('loading');
      if (loadingEl) { loadingEl.textContent = `An error occurred: ${e?.message ?? String(err)}`; }
      (window as any).PlayoffPredictor?.Spin?.hide?.();
    });

    const bracketsEl = document.getElementById('brackets');
    if (bracketsEl) bracketsEl.style.display = 'none';

    document.addEventListener('click', (e) => {
      const target = (e.target as Element).closest('#bracketUpload');
      if (!target) return;
      const brackets = document.getElementById('brackets');
      if (!brackets) return;
      brackets.querySelectorAll('a').forEach((a) => a.remove());
      brackets.insertAdjacentHTML('beforeend', `<span class="pull-right">Uploading...</span>`);
      const canvas = document.querySelector('canvas') as HTMLCanvasElement | null;
      if (!canvas) return;
      const dataUrl = canvas.toDataURL('image/jpeg', 1.0);
      ppFetch<any>({
        url: '/hockey/CustomLeague/UploadBracket',
        method: 'POST',
        data: {
          Base64: dataUrl,
          LeagueGuid: (window as any).leagueGuid,
          SaveString: SchedulePickerBase.getQueryVariable('L') || '' || localStorage.getItem((window as any).leagueGuid),
        },
      }).then((ajaxResult) => {
        brackets.querySelectorAll('span').forEach((s) => s.remove());
        brackets.insertAdjacentHTML('beforeend', `<a href="${getWebAppBaseUrl()}/${ajaxResult.Model.link}" class="pull-right">Bracket Link</a>`);
      });
    });
  }

  setExport(): void {
    // Export function intentionally stubbed (same as original)
    return;
  }

  previewHtml(): void {
    const win = window.open(
      '',
      'Title',
      'toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=yes, resizable=yes, width=780, height=200, top=' +
        (screen.height - 400) +
        ', left=' +
        (screen.width - 840)
    );
    win.document.body.innerHTML = this.HtmlExport();
  }

  init(replaceTeams?: any[]): void {
    const self = this;
    const allTeams: KnockoutObservable<ITeam>[] = [];

    const WesternDivisions = this.Dict['Western'];
    for (const divisionInd in WesternDivisions) {
      const division = WesternDivisions[divisionInd];
      for (const teamInd in division) {
        allTeams.push(division[teamInd]);
      }
    }

    const EasternDivisions = this.Dict['Eastern'];
    for (const divisionInd in EasternDivisions) {
      const division = EasternDivisions[divisionInd];
      for (const teamInd in division) {
        allTeams.push(division[teamInd]);
      }
    }
    this.AllTeams(allTeams);

    if (replaceTeams) {
      for (const teamOverride of replaceTeams) {
        const teamReplaced: any = Enumerable.From(this.AllTeams()).First(
          (p: any) => p().TeamID === teamOverride.TeamReplaced
        );
        this.AllTeams.remove(teamReplaced);
        teamReplaced().RegionName = teamOverride.RegionName;
        teamReplaced().TeamName = teamOverride.TeamName;
        teamReplaced().Conference = teamOverride.Conference;
        teamReplaced().Division = teamOverride.Division;
        this.AllTeams.push(teamReplaced);
      }
    }

    this.SelectedTeam.subscribe((newVal) => {
      if (newVal) {
        this.IsTeamSelected(true);
        setTimeout(() => {
          const teamAdbox = document.getElementById('team-adbox') as HTMLElement | null;
          const teamStatsTable = document.getElementById('team-statstable') as HTMLElement | null;
          if (teamAdbox && teamStatsTable) {
            teamAdbox.style.width = teamStatsTable.offsetWidth + 'px';
            teamAdbox.style.height = (teamStatsTable.offsetHeight * 3) + 'px';
            const ins = teamAdbox.querySelector('ins') as HTMLElement | null;
            if (ins) ins.style.height = (teamAdbox.offsetHeight * 2) + 'px';
          }
          // Bootstrap dropdown init for #team-breakout .dropdown-toggle is handled by Bootstrap JS directly
        }, 1000);
        newVal.Schedule(this.getScheduleForTeam(newVal));
        newVal.FakeObs.valueHasMutated();
      }
    });
  }

  unselectGame(gameInput: KnockoutObservable<IGame>, skipUpdate?: boolean): void {
    const self = this;
    gameInput().selectionMade(false);
    gameInput().teamSelected('NONE');
    self.GameArray[gameInput().gameScheduleNum()] = 0;
    self.updateSaveString();
    if (!skipUpdate) {
      self.UpdateStats(-1, true);
    }
  }

  load(urlString: string): void {
    const self = this;
    const numGames = self.NumGames();
    self.BlockHistoryPush(true);
    let biggestWeek = 1;

    if (self.IsAfterFirstLoad) {
      for (const weekInd in self.Weeks) {
        const gameType = self.Weeks[weekInd];
        ko.utils.arrayForEach(gameType, (week: any) => {
          // Regular-season-style games — flat slot.games via GamesComputed.
          ko.utils.arrayForEach(week.GamesComputed(), (game: KnockoutObservable<any>) => {
            game().selectionType(SelectionType.NONE);
            game().selectionMade(false);
            game().teamSelected('');
          });
          // Playoff series-style games live in week.Series[i].games — invisible
          // to GamesComputed. Without this, an IsAfterFirstLoad reload would
          // leave stale playoff selections behind, which the auto-advance loop
          // would then surface as the "latest active round."
          ko.utils.arrayForEach(week.Series(), (series: any) => {
            ko.utils.arrayForEach(series.games(), (game: KnockoutObservable<any>) => {
              game().selectionType(SelectionType.NONE);
              game().selectionMade(false);
              game().teamSelected('');
            });
          });
        });
      }
      const bracketsContainer = document.getElementById('brackets');
      if (bracketsContainer) bracketsContainer.innerHTML = '';

      for (let i = 0; i < self.AllTeams().length; i++) {
        const team = self.AllTeams()[i]() as NhlTeam;
        team.reset();
        team.FakeObs();
      }
    }

    for (let i = 0; i < urlString.length; i++) {
      const game = self.GameScheduleGameMap[i];
      if (!game) continue;
      const _game = game();
      const home = _game.home() as NhlTeam;
      const away = _game.away() as NhlTeam;
      const weekInd = _game.week;

      const input = {
        gameGuid: _game.gameGuid,
        home: {
          Conference: home.Conference,
          Division: home.Division,
          TeamName: home.TeamName,
          Pct: home.GameRecords.AllRecord.Pct,
        },
        away: {
          Conference: away.Conference,
          Division: away.Division,
          TeamName: away.TeamName,
          Pct: away.GameRecords.AllRecord.Pct,
        },
        selectionType: ko.observable<any>(),
      };

      const skipUpdate = i < numGames;
      let selection = parseInt(urlString[i], 10);
      if (!selection) {
        selection = 0;
      }
      input.selectionType(selection);

      switch (selection) {
        case SelectionType.AWAY_WIN:
        case SelectionType.AWAY_WIN_OVERTIME:
        case SelectionType.AWAY_WIN_SHOOTOUT:
          self.GameArray[i] = selection;
          if (biggestWeek < +weekInd) {
            biggestWeek = +weekInd;
          }
          if (i < numGames) {
            (away.Wins as KnockoutObservableArray<any>).push(input);
            (home.Losses as KnockoutObservableArray<any>).push(input);
            self.gameSelected(game, game().away, selection, i, skipUpdate);
          } else {
            self.selectSeriesGame(game, game().away, selection, true);
          }
          break;

        case SelectionType.HOME_WIN:
        case SelectionType.HOME_WIN_OVERTIME:
        case SelectionType.HOME_WIN_SHOOTOUT:
          self.GameArray[i] = selection;
          if (biggestWeek < +weekInd) {
            biggestWeek = +weekInd;
          }
          if (i < numGames) {
            (away.Losses as KnockoutObservableArray<any>).push(input);
            (home.Wins as KnockoutObservableArray<any>).push(input);
            self.gameSelected(game, game().home, selection, i, skipUpdate);
          } else {
            self.selectSeriesGame(game, game().home, selection, true);
          }
          break;

        case SelectionType.NONE:
          self.GameArray[i] = SelectionType.NONE;
          if (i < numGames) {
            self.unselectGame(game, skipUpdate);
          } else {
            self.selectSeriesGame(game, null, 0, true);
          }
          break;
      }

      if (i === numGames - 1) {
        super.initTeamSubscribers();
        this.UpdateStats(i, true);
      }

      if (i === this.DivisionStartPosition() && i !== 0) {
        self.seedDv();
      }
      if (i === this.ConferenceChampionshipStartPosition() && i !== 0) {
        self.seedCg();
      }
    }

    // biggestWeek here is the highest week with picks IN THE URL STRING ONLY.
    // The official-playoff overlay applies AFTER load() returns, and may push
    // the user past this point. The UrlString subscriber's auto-advance loop
    // is responsible for re-setting BOTH SelectedWeek AND BiggestWeek if a
    // higher round has overlay-applied selections — see line ~340 of this
    // file. If you ever refactor that loop, make sure it still updates both:
    // setCurrentSeason's `.finally` block reads BiggestWeek to pick the final
    // SelectedWeek, so SelectedWeek alone isn't enough.
    self.switchContext({ key: biggestWeek, value: self.Weeks[biggestWeek] });
    self.BiggestWeek(biggestWeek);

    if (self.Hash()) {
      try {
        const team = self.findTeam(self.Hash().slice(1))();
        self.showTeamStats(team);
        self.IsTeamSelected(true);
      } catch (ex) {
        console.warn(ex);
      }
    }

    self.IsAfterFirstLoad = true;
    self.BlockHistoryPush(true);
    self.pushHistory();
    self.BlockHistoryPush(false);
  }

  randomize(): void {
    const self = this;
    if (confirm('All of your regular season selections will be randomized, replacing your current selections. Are you sure you want to proceed?')) {
      for (let i = 0; i < self.NumGames(); i++) {
        let selection: SelectionType = null;
        const winner = Math.floor(Math.random() * 2) + 1;
        const random = Math.floor(Math.random() * 100) + 1;
        if (random < 20 && random > 10) {
          selection = winner === 1 ? SelectionType.HOME_WIN_OVERTIME : SelectionType.AWAY_WIN_OVERTIME;
        } else if (random < 5) {
          selection = winner === 1 ? SelectionType.HOME_WIN_SHOOTOUT : SelectionType.AWAY_WIN_SHOOTOUT;
        } else {
          selection = winner as SelectionType;
        }
        self.GameArray[i] = selection;
      }
      for (let i = self.NumGames(); i < self.FinalsStartPosition() + 7; i++) {
        self.GameArray[i] = 0;
      }
      self.updateSaveString();
      self.load(self.GameArray.join(''));

      for (const team of self.AllTeams()) {
        (team() as NhlTeam).FakeObs.valueHasMutated();
        team().Wins.valueHasMutated();
        team().Losses.valueHasMutated();
      }
      self.UpdateStats(-1, true);
      self.SelectedWeek({ key: self.MaxWeek(), value: self.Weeks[self.MaxWeek()] });
    }
  }

  randomizeWeek(): void {
    const self = this;
    for (const week of self.SelectedWeek().value as NhlWeek[]) {
      for (const gameObs of week.games()) {
        const game = gameObs() as NhlGame;
        self.unselectGame(gameObs, true);
        let selection: SelectionType = 0 as SelectionType;
        selection = (Math.floor(Math.random() * 2) + 1) as SelectionType;

        if (self.SelectedWeek().key <= self.MaxWeek()) {
          const winner = Math.floor(Math.random() * 2) + 1;
          const random = Math.floor(Math.random() * 100) + 1;
          if (random < 20 && random > 10) {
            selection = winner === 1 ? SelectionType.HOME_WIN_OVERTIME : SelectionType.AWAY_WIN_OVERTIME;
          } else if (random === 5) {
            selection = winner === 1 ? SelectionType.HOME_WIN_SHOOTOUT : SelectionType.AWAY_WIN_SHOOTOUT;
          }
        }

        let teamSelected: KnockoutObservable<ITeam>;
        switch (selection) {
          case SelectionType.AWAY_WIN:
          case SelectionType.AWAY_WIN_OVERTIME:
          case SelectionType.AWAY_WIN_SHOOTOUT:
            teamSelected = game.away;
            break;
          case SelectionType.HOME_WIN:
          case SelectionType.HOME_WIN_OVERTIME:
          case SelectionType.HOME_WIN_SHOOTOUT:
            teamSelected = game.home;
            break;
          default:
            teamSelected = game.home;
            break;
        }
        self.selectGame(gameObs, teamSelected, selection, true);

        if (game.TeamThatWon() && game.TeamThatWon()()) {
          (game.TeamThatWon()() as NhlTeam).GameRecords.valueHasMutated();
        }
        if (game.TeamThatLost() && game.TeamThatLost()()) {
          (game.TeamThatLost()() as NhlTeam).GameRecords.valueHasMutated();
        }
      }

      for (const series of week.Series()) {
        series.reset();
      }
      for (const series of week.Series()) {
        for (const gameObs of series.games()) {
          if (series.seriesDecided()) break;
          const game = gameObs() as NhlGame;
          self.unselectGame(gameObs, true);
          const selection = (Math.floor(Math.random() * 2) + 1) as SelectionType;

          let teamSelected: KnockoutObservable<ITeam>;
          switch (selection) {
            case SelectionType.AWAY_WIN:
              teamSelected = game.away;
              break;
            case SelectionType.HOME_WIN:
              teamSelected = game.home;
              break;
            default:
              teamSelected = game.home;
              break;
          }
          self.selectSeriesGame(gameObs, teamSelected, selection, true);
        }
        series.games.valueHasMutated();
        series.seriesDecided();
      }
    }
    self.UpdateStats(-1, self.SelectedWeek().key <= self.MaxWeek());
  }

  resetWeek(): void {
    const self = this;
    for (const week of self.SelectedWeek().value as NhlWeek[]) {
      for (const gameObs of week.games()) {
        const game = gameObs() as NhlGame;
        if (game.selectionMade()) {
          if (game.selectionType() !== SelectionType.TIE) {
            self.selectGame(gameObs, game.TeamThatWon(), SelectionType.NONE, true);
          } else {
            self.selectGame(gameObs, 'TIE' as any, SelectionType.NONE, true);
          }
        }
      }
      for (const series of week.Series()) {
        for (const gameObs of series.games()) {
          self.unselectGame(gameObs, true);
        }
      }
    }
    self.UpdateStats(-1, self.SelectedWeek().key <= self.MaxWeek());
  }

  selectGame(
    gameInput: KnockoutObservable<IGame>,
    selectedTeam: any,
    selectionType: SelectionType,
    isRandomize = false
  ): void {
    const self = this;
    document.querySelectorAll('div.open').forEach((el) => el.classList.remove('open'));
    const _gameInput = gameInput();
    const home = _gameInput.home() as NhlTeam;
    const away = _gameInput.away() as NhlTeam;
    const input = {
      gameGuid: _gameInput.gameGuid,
      home: {
        Conference: home.Conference,
        Division: home.Division,
        TeamName: home.TeamName,
        Pct: home.GameRecords.AllRecord.Pct,
      },
      away: {
        Conference: away.Conference,
        Division: away.Division,
        TeamName: away.TeamName,
        Pct: away.GameRecords.AllRecord.Pct,
      },
      selectionType: ko.observable(selectionType),
    };

    self.GameArray[_gameInput.gameScheduleNum()] = selectionType;
    const existingGame = self.GameScheduleGameMap[_gameInput.gameScheduleNum()]();

    if (
      typeof selectedTeam === 'function' &&
      selectedTeam().TeamAbbreviation === _gameInput.teamSelected() &&
      existingGame.selectionType() === selectionType
    ) {
      _gameInput.selectionMade(false);
      _gameInput.teamSelected('NONE');
      _gameInput.selectionType(SelectionType.NONE);
      self.GameArray[_gameInput.gameScheduleNum()] = 0;
      (home.Wins as KnockoutObservableArray<any>).remove((p: any) => p.gameGuid === input.gameGuid);
      (away.Wins as KnockoutObservableArray<any>).remove((p: any) => p.gameGuid === input.gameGuid);
      (home.Losses as KnockoutObservableArray<any>).remove((p: any) => p.gameGuid === input.gameGuid);
      (away.Losses as KnockoutObservableArray<any>).remove((p: any) => p.gameGuid === input.gameGuid);
    } else {
      (home.Wins as KnockoutObservableArray<any>).remove((p: any) => p.gameGuid === input.gameGuid);
      (away.Wins as KnockoutObservableArray<any>).remove((p: any) => p.gameGuid === input.gameGuid);
      (home.Losses as KnockoutObservableArray<any>).remove((p: any) => p.gameGuid === input.gameGuid);
      (away.Losses as KnockoutObservableArray<any>).remove((p: any) => p.gameGuid === input.gameGuid);

      _gameInput.selectionMade(true);
      _gameInput.selectionType(selectionType);

      switch (selectionType) {
        case SelectionType.AWAY_WIN:
        case SelectionType.AWAY_WIN_SHOOTOUT:
        case SelectionType.AWAY_WIN_OVERTIME:
          _gameInput.teamSelected(away.TeamAbbreviation);
          break;
        case SelectionType.HOME_WIN:
        case SelectionType.HOME_WIN_SHOOTOUT:
        case SelectionType.HOME_WIN_OVERTIME:
          _gameInput.teamSelected(home.TeamAbbreviation);
          break;
        case SelectionType.NONE:
          _gameInput.teamSelected('NONE');
          break;
      }

      if (_gameInput.week <= self.MaxWeek()) {
        switch (selectionType) {
          case SelectionType.AWAY_WIN:
          case SelectionType.AWAY_WIN_OVERTIME:
          case SelectionType.AWAY_WIN_SHOOTOUT:
            _gameInput.teamSelected(away.TeamAbbreviation);
            (away.Wins as KnockoutObservableArray<any>).push(input);
            (home.Losses as KnockoutObservableArray<any>).push(input);
            break;
          case SelectionType.HOME_WIN:
          case SelectionType.HOME_WIN_OVERTIME:
          case SelectionType.HOME_WIN_SHOOTOUT:
            _gameInput.teamSelected(home.TeamAbbreviation);
            (home.Wins as KnockoutObservableArray<any>).push(input);
            (away.Losses as KnockoutObservableArray<any>).push(input);
            break;
          case SelectionType.NONE:
            _gameInput.teamSelected('NONE');
            break;
        }
      }
    }

    clearTimeout(self.Timeout);
    self.Timeout = setTimeout(() => {
      if (gameInput().week <= self.MaxWeek()) {
        if (home.Conference === away.Conference) {
          self.UpdateRankings(home.Conference, true, -1);
        } else {
          self.UpdateRankings(Conferences.Western, true, -1);
          self.UpdateRankings(Conferences.Eastern, true, -1);
        }
      }
      super.finishComputing();
      self.updateSaveString();
      self.pushHistory();
    }, 400);
  }

  selectSeriesGame(
    gameInput: KnockoutObservable<IGame>,
    selectedTeam: any,
    selectionType: SelectionType,
    isRandomize: boolean,
    forceSet: boolean = false
  ): void {
    const self = this;
    const _gameInput = gameInput();
    const home = _gameInput.home() as NhlTeam;
    const away = _gameInput.away() as NhlTeam;

    self.GameArray[_gameInput.gameScheduleNum()] = selectionType;
    const maxWeek = self.MaxWeek();
    const gameWeek = gameInput().week;

    // Toggle-off semantics: clicking a team that's already selected clears the slot.
    // The official-playoffs overlay must NOT toggle — it always sets to the IRL outcome.
    // Pass forceSet=true to bypass the toggle and always apply the value.
    if (!forceSet && typeof selectedTeam === 'function' && selectedTeam().TeamAbbreviation === _gameInput.teamSelected()) {
      _gameInput.selectionMade(false);
      _gameInput.teamSelected('NONE');
      _gameInput.selectionType(SelectionType.NONE);
      self.GameArray[_gameInput.gameScheduleNum()] = SelectionType.NONE;
    } else {
      // Zero-valued playoff slots in a save string are unpicked games.
      _gameInput.selectionMade(selectionType !== SelectionType.NONE);
      _gameInput.selectionType(selectionType);
      switch (selectionType) {
        case SelectionType.AWAY_WIN:
        case SelectionType.AWAY_WIN_OVERTIME:
        case SelectionType.AWAY_WIN_SHOOTOUT:
          _gameInput.teamSelected(away.TeamAbbreviation);
          break;
        case SelectionType.HOME_WIN:
        case SelectionType.HOME_WIN_OVERTIME:
        case SelectionType.HOME_WIN_SHOOTOUT:
          _gameInput.teamSelected(home.TeamAbbreviation);
          break;
        case SelectionType.NONE:
          _gameInput.teamSelected('NONE');
          break;
      }
    }

    if (gameWeek === maxWeek + 1) {
      self.seedDv();
    }
    if (gameWeek === maxWeek + 2) {
      self.seedCg();
    }
    if (gameWeek === maxWeek + 3) {
      self.seedFinals();
    }

    clearTimeout(self.Timeout);
    self.Timeout = setTimeout(() => {
      this.UpdateStatsPostSeason();
      super.finishComputing();
    }, 250);
  }

  clearSeries(series: Series | ISeries): void {
    const self = this;
    for (const game of series.games()) {
      const _gameInput = game();
      const gameNum = _gameInput.gameScheduleNum();
      if (gameNum || gameNum === 0) {
        self.GameArray[gameNum] = SelectionType.NONE;
      }
    }
    clearTimeout(self.Timeout);
    self.Timeout = setTimeout(() => {
      self.updateSaveString();
      self.pushHistory();
      super.finishComputing();
    }, 250);
  }

  gameSelected(
    gameInput: KnockoutObservable<IGame>,
    selectedTeam: any,
    selectionType: SelectionType,
    positionCounter?: number,
    skipUpdate?: boolean
  ): void {
    const self = this;
    gameInput().teamSelected(selectedTeam().TeamAbbreviation);
    gameInput().selectionMade(true);
    gameInput().selectionType(selectionType);
    if (!skipUpdate) {
      self.UpdateStats(positionCounter, positionCounter === self.NumGames() - 1);
    }
  }

  SwapSeriesSeeds(seriesToSwap: Series, expectedSeeds: number[]): void {
    const winner = seriesToSwap.seriesWinner();
    const loser = seriesToSwap.seriesLoser();
    if (!(expectedSeeds.includes((winner() as NhlTeam).ConferenceRank()) && expectedSeeds.includes((loser() as NhlTeam).ConferenceRank()))) {
      this.UpdateStats(-1, true);
    }
    const winnerTeam = winner() as NhlTeam;
    const loserTeam = loser() as NhlTeam;
    if (winnerTeam.ConferenceRank() > loserTeam.ConferenceRank()) {
      const winnerRank = winnerTeam.ConferenceRank();
      const loserRank = loserTeam.ConferenceRank();
      loserTeam.ConferenceRank(winnerRank);
      winnerTeam.ConferenceRank(loserRank);
    }
    loserTeam.ConferenceRank.valueHasMutated();
    winnerTeam.ConferenceRank.valueHasMutated();
  }

  UpdateRankings(conferenceName: string, fillWildCard: boolean, Position: number): void {
    const self = this;
    const topFunc = conferenceName === 'Western' ? this.WesternTop : this.EasternTop;
    const teams: KnockoutObservable<NhlTeam>[] = [];
    const teamsInConference = this.Dict[conferenceName];

    if (fillWildCard || Position === -1) {
      for (const _conference in teamsInConference) {
        const conference = teamsInConference[_conference];
        for (const _team in conference) {
          teams.push(conference[_team] as KnockoutObservable<NhlTeam>);
        }
      }

      for (const team of teams) {
        team().Tiebreakers({
          HTH: [],
          Sweeps: [],
          DivHTH: [],
          Explanations: [],
        } as any);
      }

      const teamsSorted = this.ConferenceRanker(teams as any, false) as Array<KnockoutObservable<NhlTeam>>;
      topFunc(teamsSorted as any);

      const teamsGrouped = Enumerable.From(teamsSorted)
        .Where((p: KnockoutObservable<NhlTeam>) => p != undefined)
        .GroupBy((g: KnockoutObservable<NhlTeam>) => g().Division, (v: any) => v)
        .ToArray();

      let wcRanker = teamsSorted.slice(0);
      for (const team of teamsSorted) {
        team().InPlayoffs(false);
        team().InWildcard(false);
      }

      const firstDivTop = this.ConferenceRanker(
        Enumerable.From(teamsGrouped[0].source).ToArray() as KnockoutObservable<ITeam>[]
      );
      const secondDivTop = this.ConferenceRanker(
        Enumerable.From(teamsGrouped[1].source).ToArray() as KnockoutObservable<ITeam>[]
      );

      const appVm = this;
      if (conferenceName === 'Eastern') {
        appVm.WildcardEastern([]);
        appVm.WildcardMetropolitan([]);
        appVm.WildcardAtlantic([]);
      } else {
        appVm.WildcardWestern([]);
        appVm.WildcardPacific([]);
        appVm.WildcardCentral([]);
      }

      let divRank = 1;
      for (const team of firstDivTop as KnockoutObservable<NhlTeam>[]) {
        team().DivisionRank(divRank++);
      }
      divRank = 1;
      for (const team of secondDivTop as KnockoutObservable<NhlTeam>[]) {
        team().DivisionRank(divRank++);
      }

      divRank = 1;
      for (const team of (firstDivTop as KnockoutObservable<NhlTeam>[]).slice(0, 3)) {
        const divName = team().Division.substring(0, 1);
        team().PlayoffRank(`${divName}${divRank}`);
        team().WildcardRank(divRank++);
        team().InPlayoffs(true);
        wcRanker.splice(wcRanker.indexOf(team), 1);
        (self as any)[`Wildcard${team().Division}`].push(team);
      }

      divRank = 1;
      for (const team of (secondDivTop as KnockoutObservable<NhlTeam>[]).slice(0, 3)) {
        const divName = team().Division.substring(0, 1);
        team().PlayoffRank(`${divName}${divRank}`);
        team().WildcardRank(divRank++);
        team().InPlayoffs(true);
        wcRanker.splice(wcRanker.indexOf(team), 1);
        (self as any)[`Wildcard${team().Division}`].push(team);
      }

      wcRanker = this.ConferenceRanker(wcRanker as any) as KnockoutObservable<NhlTeam>[];
      divRank = 1;
      for (const team of wcRanker) {
        if (divRank <= 2) {
          team().InWildcard(true);
          team().PlayoffRank(`W${divRank}`);
        } else {
          team().InWildcard(false);
        }
        team().WildcardRank(divRank++);
        (self as any)[`Wildcard${team().Conference}`].push(team);
      }

      ko.utils.arrayForEach(teamsSorted, (team: KnockoutObservable<NhlTeam>, index: number) => {
        team().ConferenceRank(index + 1);
        team().Tiebreakers.valueHasMutated();
      });

      self.seedWc(conferenceName, fillWildCard);
    }
  }

  seedWc(confName: string, fillWildcard: boolean): void {
    if (!fillWildcard) return;
    const self = this;
    const wcNum = confName === 'Western' ? self.NumGames() - 1 : self.NumGames() + 27;
    const topFunc = confName === 'Western' ? self.WesternTop : self.EasternTop;

    let gNum = wcNum;
    const wcSlot = confName === 'Western' ? 0 : 2;

    (self.Weeks[self.MaxWeek() + 1][wcSlot] as NhlWeek).Series([]);
    const divLeaders = this.ConferenceRanker(
      topFunc().filter((p: any) => p().DivisionRank() === 1) as any,
      true
    ) as KnockoutObservable<NhlTeam>[];
    if (!divLeaders.length) return;

    const wcTeams = this.ConferenceRanker(
      topFunc().filter((p: any) => p().InWildcard()) as any,
      true
    ) as KnockoutObservable<NhlTeam>[];
    const div1 = this.ConferenceRanker(
      topFunc().filter(
        (p: any) => divLeaders[0]().Division === p().Division && p().DivisionRank() !== 1 && p().InPlayoffs()
      ) as any,
      true
    ) as KnockoutObservable<NhlTeam>[];
    const div2 = this.ConferenceRanker(
      topFunc().filter(
        (p: any) => divLeaders[1]().Division === p().Division && p().DivisionRank() !== 1 && p().InPlayoffs()
      ) as any,
      true
    ) as KnockoutObservable<NhlTeam>[];

    gNum = this.generateSeries(self.Weeks[self.MaxWeek() + 1][wcSlot] as NhlWeek, divLeaders[0], wcTeams[1], self.MaxWeek() + 1, 7, gNum, false);
    gNum = this.generateSeries(self.Weeks[self.MaxWeek() + 1][wcSlot] as NhlWeek, divLeaders[1], wcTeams[0], self.MaxWeek() + 1, 7, gNum, false);
    gNum = this.generateSeries(self.Weeks[self.MaxWeek() + 1][wcSlot] as NhlWeek, div1[0], div1[1], self.MaxWeek() + 1, 7, gNum, false);
    gNum = this.generateSeries(self.Weeks[self.MaxWeek() + 1][wcSlot] as NhlWeek, div2[0], div2[1], self.MaxWeek() + 1, 7, gNum, false);

    if (gNum > this.DivisionStartPosition()) {
      this.DivisionStartPosition(gNum);
    }
    this.clearSeries((self.Weeks[self.MaxWeek() + 1][wcSlot] as NhlWeek).Series()[0]);
    (self.Weeks[self.MaxWeek() + 2][wcSlot] as NhlWeek).Series([]);
    (self.Weeks[self.MaxWeek() + 3][wcSlot] as NhlWeek).Series([]);
    (self.Weeks[self.MaxWeek() + 4][wcSlot] as NhlWeek).Series([]);
  }

  seedDv(): void {
    const self = this;
    const wcWeek = self.MaxWeek() + 1;
    const dvWeek = self.MaxWeek() + 2;
    const WesternSeries = (self.Weeks[wcWeek][0] as NhlWeek).Series();
    const EasternSeries = (self.Weeks[wcWeek][2] as NhlWeek).Series();
    let gNum = this.DivisionStartPosition();

    if (WesternSeries.length === 4 && WesternSeries.every((s) => s.seriesDecided())) {
      (self.Weeks[dvWeek][0] as NhlWeek).Series([]);
      gNum = this.generateSeries(self.Weeks[dvWeek][0] as NhlWeek, WesternSeries[0].seriesWinner(), WesternSeries[2].seriesWinner(), dvWeek, 7, gNum, false);
      gNum = this.generateSeries(self.Weeks[dvWeek][0] as NhlWeek, WesternSeries[1].seriesWinner(), WesternSeries[3].seriesWinner(), dvWeek, 7, gNum, false);
      this.clearSeries((self.Weeks[dvWeek][0] as NhlWeek).Series()[0]);
      this.clearSeries((self.Weeks[dvWeek][0] as NhlWeek).Series()[1]);
    }

    if (EasternSeries.length === 4 && EasternSeries.every((s) => s.seriesDecided())) {
      (self.Weeks[dvWeek][2] as NhlWeek).Series([]);
      gNum = this.generateSeries(self.Weeks[dvWeek][2] as NhlWeek, EasternSeries[0].seriesWinner(), EasternSeries[2].seriesWinner(), dvWeek, 7, gNum, false);
      gNum = this.generateSeries(self.Weeks[dvWeek][2] as NhlWeek, EasternSeries[1].seriesWinner(), EasternSeries[3].seriesWinner(), dvWeek, 7, gNum, false);
      this.clearSeries((self.Weeks[dvWeek][2] as NhlWeek).Series()[0]);
      this.clearSeries((self.Weeks[dvWeek][2] as NhlWeek).Series()[1]);
    }

    if (gNum > this.ConferenceChampionshipStartPosition()) {
      this.ConferenceChampionshipStartPosition(gNum);
    }
  }

  seedCg(): void {
    const self = this;
    const dvWeek = self.MaxWeek() + 2;
    const cgWeek = self.MaxWeek() + 3;
    const WesternSeries = (self.Weeks[dvWeek][0] as NhlWeek).Series();
    const EasternSeries = (self.Weeks[dvWeek][2] as NhlWeek).Series();
    let gNum = this.ConferenceChampionshipStartPosition();

    if (WesternSeries.length === 2 && WesternSeries.every((s) => s.seriesDecided())) {
      (self.Weeks[cgWeek][0] as NhlWeek).Series([]);
      gNum = this.generateSeries(self.Weeks[cgWeek][0] as NhlWeek, WesternSeries[0].seriesWinner(), WesternSeries[1].seriesWinner(), cgWeek, 7, gNum, true);
      this.clearSeries((self.Weeks[cgWeek][0] as NhlWeek).Series()[0]);
    }

    if (EasternSeries.length === 2 && EasternSeries.every((s) => s.seriesDecided())) {
      (self.Weeks[cgWeek][2] as NhlWeek).Series([]);
      gNum = this.generateSeries(self.Weeks[cgWeek][2] as NhlWeek, EasternSeries[0].seriesWinner(), EasternSeries[1].seriesWinner(), cgWeek, 7, gNum, true);
      this.clearSeries((self.Weeks[cgWeek][2] as NhlWeek).Series()[0]);
    }

    if (gNum > this.FinalsStartPosition()) {
      this.FinalsStartPosition(gNum);
    }
  }

  seedFinals(): void {
    const self = this;
    const cgWeek = self.MaxWeek() + 3;
    const finalsWeek = self.MaxWeek() + 4;
    const WesternSeries = (self.Weeks[cgWeek][0] as NhlWeek).Series();
    const EasternSeries = (self.Weeks[cgWeek][2] as NhlWeek).Series();
    let gNum = this.FinalsStartPosition();

    if (
      WesternSeries.length === 1 &&
      WesternSeries.every((s) => s.seriesDecided()) &&
      EasternSeries.length === 1 &&
      EasternSeries.every((s) => s.seriesDecided())
    ) {
      (self.Weeks[finalsWeek][1] as NhlWeek).Series([]);
      gNum = this.generateSeries(
        self.Weeks[finalsWeek][1] as NhlWeek,
        WesternSeries[0].seriesWinner(),
        EasternSeries[0].seriesWinner(),
        finalsWeek,
        7,
        gNum,
        true
      );
      this.clearSeries((self.Weeks[finalsWeek][1] as NhlWeek).Series()[0]);
    }
  }

  generateSeries(
    week: NhlWeek,
    team1: KnockoutObservable<NhlTeam> | KnockoutObservable<ITeam>,
    team2: KnockoutObservable<NhlTeam> | KnockoutObservable<ITeam>,
    weekNum: number,
    numGames: number,
    gameScheduleNum: number,
    determineHomeAdvantageFromSeeds: boolean
  ): number {
    const games: KnockoutObservable<NhlGame>[] = [];
    let home = team1 as KnockoutObservable<NhlTeam>;
    let away = team2 as KnockoutObservable<NhlTeam>;

    if (determineHomeAdvantageFromSeeds) {
      const sorted = this.ConferenceRanker([team1, team2] as any, true) as KnockoutObservable<NhlTeam>[];
      home = sorted[0];
      away = sorted[1];
    }

    if (weekNum === this.MaxWeek() + 2) {
      if (home().InWildcard()) {
        const tmp = away;
        away = home;
        home = tmp;
      }
    }

    games.push(ko.observable(new NhlGame(home, away, weekNum, gameScheduleNum + 1, null, null, weekNum + '1')));
    games.push(ko.observable(new NhlGame(home, away, weekNum, gameScheduleNum + 2, null, null, weekNum + '2')));
    games.push(ko.observable(new NhlGame(away, home, weekNum, gameScheduleNum + 3, null, null, weekNum + '3')));
    games.push(ko.observable(new NhlGame(away, home, weekNum, gameScheduleNum + 4, null, null, weekNum + '4')));
    games.push(ko.observable(new NhlGame(home, away, weekNum, gameScheduleNum + 5, null, null, weekNum + '5')));
    games.push(ko.observable(new NhlGame(away, home, weekNum, gameScheduleNum + 6, null, null, weekNum + '6')));
    games.push(ko.observable(new NhlGame(home, away, weekNum, gameScheduleNum + 7, null, null, weekNum + '7')));

    for (const gameObs of games) {
      const gameUnwrapped = gameObs();
      const scheduleNum = gameUnwrapped.gameScheduleNum();
      this.GameScheduleGameMap[scheduleNum] = gameObs as unknown as KnockoutObservable<IGame>;
      this.GameArray[scheduleNum] = 0;
    }

    week.Series.push(new Series(games as unknown as KnockoutObservable<IGame>[]));
    return gameScheduleNum + numGames;
  }

  HtH(teams: KnockoutObservable<ITeam>[]): any[] {
    const hth_games: Record<string, number[]> = {};
    for (const Team in teams) {
      const left = teams[Team];
      hth_games[left().TeamAbbreviation] = [0, 0, 0];
      for (const team2 in teams) {
        if (teams[Team] !== teams[team2]) {
          const right = teams[team2];
          hth_games[right().TeamAbbreviation] = [0, 0, 0];
        }
      }
    }
    for (const Team in teams) {
      const left = teams[Team];
      for (const team2 in teams) {
        if (teams[Team] !== teams[team2]) {
          const right = teams[team2];
          const hthGames = this.getGamesVsEachOther(left, right);
          for (const hthIdx in hthGames) {
            const game = hthGames[hthIdx];
            if (game().selectionMade) {
              if (game().TeamThatWon() && game().TeamThatWon()() && game().TeamThatWon()().TeamAbbreviation === left().TeamAbbreviation) {
                hth_games[left().TeamAbbreviation][0]++;
                hth_games[right().TeamAbbreviation][1]++;
              }
              if (game().TeamThatWon() && game().TeamThatWon()() && game().TeamThatWon()().TeamAbbreviation === right().TeamAbbreviation) {
                hth_games[right().TeamAbbreviation][0]++;
                hth_games[left().TeamAbbreviation][1]++;
              }
            }
          }
        }
      }
    }
    for (const team in teams) {
      (teams[team] as any).HtHCalcPctNaN = this.CalcPctNaN(hth_games[teams[team]().TeamAbbreviation]);
      (teams[team] as any).HtHCalcPct = this.CalcPct(hth_games[teams[team]().TeamAbbreviation]);
    }
    const groups = Enumerable.From(teams).OrderByDescending((p: any) => p.HtHCalcPctNaN).GroupBy((p: any) => p.HtHCalcPctNaN).ToArray();
    return groups;
  }

  ConferenceRanker(teams: KnockoutObservable<ITeam>[], preventRankOverride?: boolean): KnockoutObservable<ITeam>[] {
    if (Enumerable.From(teams).Any((p: any) => p == undefined)) {
      return undefined;
    }

    // Build KO observable map + plain tiebreaker teams via shared adapter helpers
    const teamObsMap = buildTeamObsMap(teams);
    const plainTeams: NhlTiebreakerTeam[] = teams.map(toNhlTiebreakerTeam);

    // Build game results lookup from all team schedules (for H2H scoring).
    // NHL uses per-team schedules rather than the Weeks structure the shared
    // extractGamesFromWeeks helper consumes, so the extraction stays inline.
    const gameResults: GameResult[] = [];
    const seenGames = new Set<string>();
    for (const obs of teams) {
      const schedule = this.getScheduleForTeam(obs());
      for (const gameObs of schedule) {
        if (!gameObs) continue;
        const game = gameObs();
        if (!game || !game.selectionMade?.()) continue;
        const home = game.home?.();
        const away = game.away?.();
        if (!home || !away) continue;
        const gameKey = `${game.gameGuid ?? `${home.TeamAbbreviation}-${away.TeamAbbreviation}-${game.SortOrder?.()}`}`;
        if (seenGames.has(gameKey)) continue;
        seenGames.add(gameKey);
        const winner = game.TeamThatWon?.()?.()?.TeamAbbreviation ?? null;
        const rawSortOrder: number | undefined = game.SortOrder?.();
        const sortOrder = rawSortOrder != null && rawSortOrder !== 1 ? rawSortOrder : undefined;
        gameResults.push({
          homeTeam: home.TeamAbbreviation,
          awayTeam: away.TeamAbbreviation,
          winner,
          selectionType: game.selectionType?.() ?? 0,
          sortOrder,
        });
      }
    }
    const gamesLookup = new ArrayGameResultsLookup(gameResults);

    // Run extracted tiebreaker service, then apply results back via shared adapter
    const result = nhlConferenceRanker(plainTeams, gamesLookup);
    applyTiebreakerResult(result, teamObsMap, { preventRankOverride });
    return reorderTeamObs(result, teamObsMap);
  }

  UpdateStats(Position: number, fillWildcard?: boolean): void {
    const self = this;
    self.updateSaveString();
    self.UpdateRankings(Conferences.Western, fillWildcard, Position);
    self.UpdateRankings(Conferences.Eastern, fillWildcard, Position);
    self.pushHistory();
    document.querySelectorAll('#content .hidden').forEach((el) => el.classList.remove('hidden'));
  }

  override pushHistory(): void {
    const self = this;
    const maxWeek = self.MaxWeek();
    BracketGenerator.GenerateBracket(
      self.Weeks[maxWeek + 1],
      self.Weeks[maxWeek + 2],
      self.Weeks[maxWeek + 3],
      self.Weeks[maxWeek + 4]
    );
    super.pushHistory();
  }

  /**
   * Returns all currently-generated playoff series across all rounds and conferences.
   * Used by the official-playoff-overlay to find series by team pair.
   */
  getAllPlayoffSeries(): Series[] {
    const self = this;
    const maxWeek = self.MaxWeek();
    const result: Series[] = [];

    for (let roundOffset = 1; roundOffset <= NhlApp.postSeasonWeeks; roundOffset++) {
      const weekSlots = self.Weeks[maxWeek + roundOffset];
      if (!weekSlots) {
        continue;
      }
      for (const slot of weekSlots) {
        for (const series of (slot as NhlWeek).Series()) {
          result.push(series);
        }
      }
    }

    return result;
  }

  UpdateStatsPostSeason(): void {
    const self = this;
    self.updateSaveString();
    self.pushHistory();
    super.finishComputing();
  }

}
